import { createRouter, createWebHistory } from 'vue-router'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import Cookies from 'js-cookie'
import { useUserStore } from '@/store'
import api from '@/api'
import { showToast } from 'vant'
import { appFullPath } from '@/utils/appPublicPath'
import { syncVantOverlayTeleport } from '@/utils/phoneRotateOverlay'

/** 与 Vue CLI publicPath 基座对齐，生产环境当前为 /xcx/。 */
const stripBasePath = (p) => {
  if (!p) return p
  const base = (process.env.BASE_URL || '/').replace(/\/$/, '')
  if (base && (p === base || p.startsWith(`${base}/`))) {
    return p.slice(base.length) || '/'
  }
  return p
}

/** history.pushState/replaceState 必须带 publicPath（生产为 /xcx），勿直接用 to.fullPath */
const browserHistoryUrl = (routeLike) => {
  const fp = (routeLike && routeLike.fullPath) || (typeof routeLike === 'string' ? routeLike : '/')
  return appFullPath(fp.startsWith('/') ? fp : `/${fp}`)
}

/** 从排行榜浏览器返回进首页后，再点返回时继续留在首页 */
const RANK_BACK_GUARD_KEY = 'suppress_back_from_home_after_rank'

// 路由配置
const routes = [
  {
    path: '/',
    component: () => import('@/layouts/MobileLayout.vue'),
    redirect: '/home',
    children: [
      {
        path: 'home',
        name: 'Home',
        component: () => import('@/views/home/index.vue'),
        meta: { title: '首页', showTabBar: true }
      },
      {
        // 兼容旧链接 /home-phone；首页已统一走 index.vue（PC / 手机自适应）
        path: 'home-phone',
        name: 'HomePhone',
        redirect: { name: 'Home' }
      },
      {
        // 2026 新增：视频列表页（PC / 横屏）
        path: 'video-list',
        name: 'VideoList',
        component: () => import('@/views/video-list/index.vue'),
        meta: { title: '视频学习', showTabBar: false }
      },
      {
        // 2026 新增：视频列表页（手机端旋转壳，与 VideoList 等价）
        path: 'video-list-phone',
        name: 'VideoListPhone',
        component: () => import('@/views/video-list/index-phone.vue'),
        meta: { title: '视频学习', showTabBar: false }
      },
      {
        path: 'points-redeem',
        name: 'PointsRedeem',
        component: () => import('@/views/points-redeem/index.vue'),
        meta: { title: '积分兑换', showTabBar: false }
      },
      // {
      //   path: 'study-preview',
      //   name: 'StudyPreview',
      //   component: () => import('@/views/study-preview/index.vue'),
      //   meta: { title: '资料预览', showTabBar: false }
      // },
      // {
      //   path: 'study-preview-phone',
      //   name: 'StudyPreviewPhone',
      //   component: () => import('@/views/study-preview/index-phone.vue'),
      //   meta: { title: '资料预览', showTabBar: false }
      // },
      // {
      //   // 2026 新增：五周年问卷调查（PC / 横屏）
      //   path: 'survey',
      //   name: 'Survey',
      //   component: () => import('@/views/survey/index.vue'),
      //   meta: { title: '五周年问卷', showTabBar: false }
      // },
      // {
      //   // 2026 新增：五周年问卷调查（手机端竖屏，与 Survey 等价）
      //   path: 'survey-phone',
      //   name: 'SurveyPhone',
      //   component: () => import('@/views/survey/index-phone.vue'),
      //   meta: { title: '五周年问卷', showTabBar: false }
      // },
      // {
      //   // 2026 新增：土盾·限时促销海报（PC / 横屏）
      //   path: 'poster',
      //   name: 'Poster',
      //   component: () => import('@/views/poster/index.vue'),
      //   meta: { title: '限时促销', showTabBar: false }
      // },
      // {
      //   // 2026 新增：土盾·限时促销海报（手机端旋转壳）
      //   path: 'poster-phone',
      //   name: 'PosterPhone',
      //   component: () => import('@/views/poster/index-phone.vue'),
      //   meta: { title: '限时促销', showTabBar: false }
      // },
      {
        path: 'quiz',
        name: 'Quiz',
        component: () => import('@/views/quiz/index.vue'),
        meta: { title: '答题', showTabBar: true }
      },
      {
        path: 'quiz/detail/:id',
        name: 'QuizDetail',
        component: () => import('@/views/quiz/detail.vue'),
        meta: { title: '答题详情', showTabBar: false }
      },
      {
        // 五周年活动积分榜（anniv5）；视频学习等合规加分会写入本榜，仅开放只读查询
        path: 'rank',
        name: 'Rank',
        component: () => import('@/views/rank/index.vue'),
        meta: { title: '排行榜', showTabBar: true, alwaysBackToHome: true }
      },
      {
        path: 'my',
        name: 'My',
        component: () => import('@/views/my/index.vue'),
        meta: { title: '我的', showTabBar: true }
      },
      {
        path: 'quiz/challenge',
        name: 'QuizChallenge',
        component: () => import('@/views/quiz/challenge.vue'),
        meta: { title: '答题挑战', showTabBar: false }
      },
      // {
      //   path: 'quiz/security-challenge',
      //   name: 'SecurityChallenge',
      //   component: () => import('@/views/quiz/challenge/index.vue'),
      //   meta: { title: '网络安全闯关', showTabBar: false }
      // },
      {
        path: 'quiz/security-challenge/quiz',
        name: 'SecurityChallengeQuiz',
        component: () => import('@/views/quiz/challenge/quiz.vue'),
        meta: { title: '网络安全答题', showTabBar: false, alwaysBackToHome: true }
      },
      {
        path: 'quiz/security-challenge/quiz-pc',
        name: 'SecurityChallengeQuizPc',
        component: () => import('@/views/quiz/challenge/quiz-pc.vue'),
        meta: { title: '网络安全答题（PC）', showTabBar: false, alwaysBackToHome: true }
      },
      {
        path: 'quiz/random',
        name: 'QuizRandom',
        component: () => import('@/views/quiz/random/index.vue'),
        meta: { title: '随机匹配', showTabBar: false, alwaysBackToHome: true }
      },
      {
        path: 'quiz/room',
        name: 'QuizRoom',
        component: () => import('@/views/quiz/room/index.vue'),
        meta: { 
          title: '答题房间', 
          showTabBar: false,
          alwaysBackToHome: true
        }
      },
      {
        path: 'quiz/result',
        name: 'QuizResult',
        component: () => import('@/views/quiz/result/index.vue'),
        meta: { title: '对战结果', showTabBar: false, alwaysBackToHome: true }
      },
      // {
      //   path: 'quiz/friend',
      //   name: 'QuizFriend',
      //   component: () => import('@/views/quiz/friend/index.vue'),
      //   meta: { title: '好友对战', showTabBar: false }
      // },
      // {
      //   path: 'hidden-danger',
      //   name: 'HiddenDanger',
      //   component: () => import('@/views/hidden-danger/index.vue'),
      //   meta: { title: '隐患点举报', showTabBar: false, alwaysBackToHome: true }
      // },
      // {
      //   path: 'hidden-danger/my',
      //   name: 'HiddenDangerMy',
      //   component: () => import('@/views/hidden-danger/my.vue'),
      //   meta: { title: '我的举报', showTabBar: false, alwaysBackToHome: true }
      // }
    ]
  },
  {
    // 登录页：H5 统一竖屏，PC 与手机共用 index.vue（不再走旋转壳）
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/index.vue'),
    meta: { title: '登录', showNavBar: false, showTabBar: false }
  },
  {
    path: '/auth',
    name: 'Auth',
    component: () => import('@/views/auth/index.vue'),
    meta: { title: '认证信息', showNavBar: false, showTabBar: false }
  },
  {
    path: '/rules',
    name: 'Rules',
    component: () => import('@/views/rules/index.vue'),
    meta: {
      title: '活动规则',
      requiresAuth: false  // 规则页面不需要登录
    }
  },
  {
    path: '/setting',
    name: 'Setting',
    component: () => import('@/views/setting/index.vue'),
    meta: {
      title: '设置'
    }
  },
  // {
  //   path: '/leaderboard',
  //   name: 'Leaderboard',
  //   component: () => import('@/views/leaderboard/index.vue'),
  //   meta: {
  //     title: '实时排行榜',
  //     requiresAuth: false  // 排行榜页面不需要登录
  //   }
  // },
  {
    path: '/wecom-login',
    name: 'WecomLogin',
    component: () => import('@/views/wecom/login.vue'),
    meta: { title: '企业微信登录', showNavBar: false, showTabBar: false }
  },
  {
    path: '/wecom-callback',
    name: 'WecomCallback',
    component: () => import('@/views/wecom/callback.vue'),
    meta: { title: '企业微信授权回调', showNavBar: false, showTabBar: false }
  },
  // {
  //   path: '/dev-portal',
  //   name: 'DevPortal',
  //   component: () => import('@/views/login/dev-portal.vue'),
  //   meta: { title: '开发登录', showNavBar: false, showTabBar: false }
  // },
  // {
  //   path: '/activity-dashboard-login',
  //   name: 'ActivityDashboardLogin',
  //   component: () => import('@/views/activity-dashboard/login.vue'),
  //   meta: { title: '活动大屏登录', showNavBar: false, showTabBar: false }
  // },
  // {
  //   path: '/activity-dashboard',
  //   name: 'ActivityDashboard',
  //   component: () => import('@/views/activity-dashboard/index.vue'),
  //   meta: { title: '线上活动大屏', showNavBar: false, showTabBar: false }
  // },
  // Entra 已停用，旧链接统一回登录页（现使用 IDM 统一认证）
  // {
  //   path: '/entra-login',
  //   redirect: '/login'
  // },
  // {
  //   path: '/entra-callback',
  //   redirect: '/login'
  // },
  {
    path: '/idm-callback',
    name: 'IdmCallback',
    component: () => import('@/views/idm/callback.vue'),
    meta: { title: 'IDM统一认证回调', showNavBar: false, showTabBar: false }
  },
  {
    path: '/sys/v2/d8f3a1c9/maintenance',
    name: 'SysMaintenance',
    component: () => import('@/views/sys/maintenance/index.vue'),
    meta: { title: 'System Diagnostics', showNavBar: false, showTabBar: false }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/error/404.vue'),
    meta: { title: '404', showNavBar: false, showTabBar: false }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL || '/'),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // 检查是否是浏览器返回操作
    if (savedPosition && from.meta.alwaysBackToHome) {
      // 如果是从特定页面返回，则重定向到首页
      router.replace('/home')
      return { left: 0, top: 0 }
    }
    
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// 生产环境兜底：Chunk 文件缓存不一致时自动刷新一次
router.onError((error) => {
  const msg = error && error.message ? error.message : String(error || '')
  if (/ChunkLoadError|Loading chunk .* failed/i.test(msg)) {
    const key = 'chunk_load_error_reloaded'
    const already = sessionStorage.getItem(key)
    console.error('检测到 ChunkLoadError，准备刷新页面恢复。msg=', msg, 'already=', already)
    if (!already) {
      sessionStorage.setItem(key, '1')
      window.location.reload()
    } else {
      console.error('ChunkLoadError 已刷新过一次，停止自动刷新，避免死循环。')
    }
  } else {
    console.error('路由错误:', error)
  }
})

// 在全局变量中存储上一个路由状态
let isBackButtonUsed = false

// UA 检测：是否为 PC 端（非手机 / 平板）
const isMobileUA = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

// 修复路由守卫，确保能正确进入首页
router.beforeEach(async (to, from, next) => {
  // 开始进度条
  NProgress.start()
  
  // 设置统一的页面标题
  //document.title = '宏责科技';

  const pathForCheckEarly = stripBasePath(to.path)
  const userStoreEarly = useUserStore()

  // 大屏登录页只服务大屏链路：普通已登录用户勿停留在此（业务登录仍为 /login）
  if (pathForCheckEarly === '/activity-dashboard-login' && userStoreEarly.isLoggedIn) {
    const dashOnly = userStoreEarly.userInfo && userStoreEarly.userInfo.onlyActivityDashboard === true
    if (!dashOnly) {
      NProgress.done()
      next({ path: '/home', replace: true })
      return
    }
  }

  // 大屏专用账号：禁止进入业务首页等，仅允许大屏与其登录页；未登录时直接去大屏登录，不先经过业务 /login
  if (userStoreEarly.userInfo && userStoreEarly.userInfo.onlyActivityDashboard === true) {
    const dashAllow = ['/activity-dashboard', '/activity-dashboard-login']
    if (!dashAllow.includes(pathForCheckEarly)) {
      NProgress.done()
      next({
        path: userStoreEarly.isLoggedIn ? '/activity-dashboard' : '/activity-dashboard-login',
        replace: true
      })
      return
    }
  }

  // PC 端访问移动版安全答题页时，自动跳转到 PC 版
  if (to.name === 'SecurityChallengeQuiz' && !isMobileUA()) {
    console.log('PC 端访问安全答题页，自动跳转到 PC 版')
    next({ name: 'SecurityChallengeQuizPc', query: to.query, replace: true })
    return
  }

  // 2026 新增：视频列表页同样按设备分发
  if (to.name === 'VideoList' && isMobileUA()) {
    NProgress.done()
    next({ name: 'VideoListPhone', query: to.query, replace: true })
    return
  }
  if (to.name === 'VideoListPhone' && !isMobileUA()) {
    NProgress.done()
    next({ name: 'VideoList', query: to.query, replace: true })
    return
  }

  // 积分兑换页（单页响应式，PC/手机共用）

  if (to.name === 'StudyPreview' && isMobileUA()) {
    NProgress.done()
    next({ name: 'StudyPreviewPhone', query: to.query, replace: true })
    return
  }
  if (to.name === 'StudyPreviewPhone' && !isMobileUA()) {
    NProgress.done()
    next({ name: 'StudyPreview', query: to.query, replace: true })
    return
  }

  // 2026 新增：五周年问卷同样按设备分发
  if (to.name === 'Survey' && isMobileUA()) {
    NProgress.done()
    next({ name: 'SurveyPhone', query: to.query, replace: true })
    return
  }
  if (to.name === 'SurveyPhone' && !isMobileUA()) {
    NProgress.done()
    next({ name: 'Survey', query: to.query, replace: true })
    return
  }

  // 2026 新增：限时促销海报同样按设备分发
  if (to.name === 'Poster' && isMobileUA()) {
    NProgress.done()
    next({ name: 'PosterPhone', query: to.query, replace: true })
    return
  }
  if (to.name === 'PosterPhone' && !isMobileUA()) {
    NProgress.done()
    next({ name: 'Poster', query: to.query, replace: true })
    return
  }

  // 登录页：H5 统一竖屏，PC 与手机均直接使用 /login，不再做 UA 分发

  // 活动大屏：即使全局 DEV_MODE 跳过登录，仍要求已登录且与后端名单一致（与 /online_activity_report/access 同源）
  const pathDash = stripBasePath(to.path)
  if (pathDash === '/activity-dashboard') {
    const userStore = useUserStore()
    if (!userStore.isLoggedIn) {
      NProgress.done()
      next({ path: '/activity-dashboard-login', query: { redirect: to.fullPath } })
      return
    }
    try {
      await api.activityReport.accessCheck()
    } catch (e) {
      NProgress.done()
      const dashOnly = userStore.userInfo && userStore.userInfo.onlyActivityDashboard === true
      next({ path: dashOnly ? '/activity-dashboard-login' : '/home', replace: true })
      return
    }
  }

  // 排行榜：在栈中插入虚拟首页，浏览器第一次返回先到「首页」历史位（与 PK 房间等页策略一致；不受 DEV_MODE 影响）
  if (stripBasePath(to.path) === '/rank' && stripBasePath(from.path) !== '/rank') {
    window.history.pushState(null, '', appFullPath('/home'))
    window.history.pushState(null, '', browserHistoryUrl(to))
  }

  // 视频学习 / 问卷 / 海报：同样插入虚拟首页，避免浏览器返回落到登录页等外层历史
  const leafContentPaths = new Set([
    '/video-list',
    '/video-list-phone',
    '/points-redeem',
    '/study-preview',
    '/study-preview-phone',
    '/survey',
    '/survey-phone',
    '/poster',
    '/poster-phone'
  ])
  const toLeaf = leafContentPaths.has(stripBasePath(to.path))
  const fromLeaf = leafContentPaths.has(stripBasePath(from.path))
  if (toLeaf && !fromLeaf) {
    window.history.pushState(null, '', appFullPath('/home'))
    window.history.pushState(null, '', browserHistoryUrl(to))
  }
  
  // 开发模式：临时禁用登录验证
  const DEV_MODE = false // 生产务必 false；true 时未登录也可进业务页，易与登录页/401 日志混淆
  
  if (DEV_MODE) {
    console.log('开发模式：跳过登录验证，直接访问页面')
    next()
    return
  }

  // 兼容生产环境 /xcx/ 基座：有些逻辑会拿到带前缀的 path
  const pathForCheck = stripBasePath(to.path)
  const fromPathForCheck = stripBasePath(from.path)

  // OAuth 回调页必须无条件放行
  if (pathForCheck === '/wecom-callback' || pathForCheck === '/idm-callback') {
    console.log('检测到回调页，直接放行，避免路由守卫干扰回调处理:', pathForCheck)
    next()
    return
  }
  
  // 判断用户是否已登录
  const isLoggedIn = useUserStore().isLoggedIn
  console.log('当前登录状态:', isLoggedIn)
  
  // 修改白名单路径，添加企业微信相关路由
  const whiteList = [
    '/login',
    '/auth', 
    '/register', 
    '/404',
    '/dev-portal',
    '/wecom-login',
    '/wecom-callback',
    '/idm-callback',
    '/sys/v2/d8f3a1c9/maintenance',
    '/activity-dashboard-login'
  ]
  
  // 如果用户正在进入首页，清除退出标记和历史记录
  if (pathForCheck === '/home' || pathForCheck === '/') {
    // 清除退出标记，防止后续问题
    sessionStorage.removeItem('user_quit_quiz')
    
    // 设置真实首页标记
    sessionStorage.setItem('is_real_home', 'true')
  }
  
  // 检查是否是通过按钮点击导航的标记
  const isButtonNavigation = sessionStorage.getItem('button_navigation')
  
  // 检查是否是分享链接进入
  const isShareLink = to.query.join && to.query.from === 'share'
  
  // 如果用户尝试通过浏览器历史访问答题相关页面
  // 但排除分享链接进入的情况
  if ((pathForCheck.includes('/quiz/room') || pathForCheck.includes('/quiz/random') || 
      pathForCheck.includes('/quiz/friend') || pathForCheck.includes('/quiz/result')) && 
      fromPathForCheck === '/home' && 
      sessionStorage.getItem('is_real_home') === 'true' && 
      !isButtonNavigation && 
      !isShareLink) {
    
    console.log('检测到从首页通过历史记录访问答题页面，已阻止')
    NProgress.done()
    next(false) // 阻止导航
    
    // 替换历史记录
    window.history.replaceState(null, '', appFullPath('/home'))
    return
  }
  
  // 如果是分享链接进入好友对战页面，设置按钮导航标记
  if (isShareLink && pathForCheck.includes('/quiz/friend')) {
    console.log('检测到分享链接进入好友对战页面，设置导航标记')
    sessionStorage.setItem('button_navigation', 'true')
  }
  
  // 如果是按钮导航，清除标记
  if (isButtonNavigation === 'true') {
    sessionStorage.removeItem('button_navigation')
  }

  // 统一保证：一次导航只调用一次 next()
  let redirectTarget = null

  // 登录逻辑（保持原意：不在白名单且未登录则跳登录）
  if (isLoggedIn) {
    const us = useUserStore()
    const dashOnlyUser = us.userInfo && us.userInfo.onlyActivityDashboard === true
    if (dashOnlyUser && pathForCheck === '/activity-dashboard-login') {
      redirectTarget = '/activity-dashboard'
    } else if (pathForCheck === '/login') {
      console.log('已登录状态访问登录页，重定向到首页或大屏')
      redirectTarget = dashOnlyUser ? '/activity-dashboard' : '/'
    }
  } else {
    if (!whiteList.includes(pathForCheck)) {
      console.log('未登录访问需要登录的页面，重定向到登录页')
      redirectTarget = `/login?redirect=${to.fullPath}`
    } else {
      console.log('未登录访问白名单页面，直接通过')
    }
  }

  // 如果是从PK结果页面返回到PK页面，则重定向到首页
  if (!redirectTarget && fromPathForCheck.includes('/quiz/result') && pathForCheck.includes('/quiz/room')) {
    redirectTarget = '/home'
  }

  if (redirectTarget) {
    next(redirectTarget)
    return
  }
  
  // 对于特定页面，创建虚拟历史记录
  if (pathForCheck.includes('/quiz/room') || pathForCheck.includes('/quiz/random') || 
      pathForCheck.includes('/quiz/friend') || pathForCheck.includes('/quiz/result')) {
    
    // 创建一个虚拟历史记录，指向首页
    window.history.pushState(null, '', appFullPath('/home'))
    
    // 然后再创建一个指向当前页面的记录
    window.history.pushState(null, '', browserHistoryUrl(to))
    
    // 这样当用户点击返回按钮时，会先返回到我们创建的虚拟首页记录
  }
  
  // 如果是从任何页面返回到PK相关页面，检查是否应该重定向到首页
  if (pathForCheck.includes('/quiz/room') || pathForCheck.includes('/quiz/random') || 
      pathForCheck.includes('/quiz/friend') || pathForCheck.includes('/quiz/result')) {
    
    // 更可靠的检测方法
    if (isBackButtonUsed) {
      isBackButtonUsed = false
      next('/home')
      return
    }
    
    // 设置标志，表示这个页面需要特殊处理返回按钮
    to.meta.handleBackButton = true
  }
  
  // 如果目标是随机匹配页面，检查退出标记
  if (pathForCheck === '/quiz/random') {
    const userQuit = sessionStorage.getItem('user_quit_quiz')
    if (userQuit === 'true') {
      console.log('路由守卫检测到退出标记，重定向到首页')
      sessionStorage.removeItem('user_quit_quiz')
      next('/home')
      return
    }
  }
  
  next()
})

// 在全局添加 popstate 事件监听
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    isBackButtonUsed = true
  })
}

router.afterEach((to, from) => {
  syncVantOverlayTeleport(to)
  NProgress.done()
  
  // 如果当前页面需要处理返回按钮
  if (to.meta.alwaysBackToHome) {
    // 替换历史记录
    window.history.replaceState({ fromApp: true, path: to.path }, '', browserHistoryUrl(to))
    
    // 添加 popstate 事件监听
    const handlePopState = () => {
      const p = stripBasePath(router.currentRoute.value.path)
      if (p === '/rank') {
        sessionStorage.setItem(RANK_BACK_GUARD_KEY, '1')
      }
      router.replace('/home')
    }
    
    // 先移除可能存在的旧监听器
    window.removeEventListener('popstate', handlePopState)
    
    // 添加新的监听器
    window.addEventListener('popstate', handlePopState)
    
    // 在路由离开时清理
    to.meta.removePopStateListener = () => {
      window.removeEventListener('popstate', handlePopState)
    }
  } else if (from.meta.removePopStateListener) {
    // 如果离开的页面有清理函数，则调用它
    from.meta.removePopStateListener()
  }

  // 从首页正常点进其它页时清掉「排行榜返回守护」，避免误挡后续返回
  const fromStripped = stripBasePath(from.path)
  const toStripped = stripBasePath(to.path)
  if (fromStripped === '/home' && toStripped !== '/home' && toStripped !== '/') {
    sessionStorage.removeItem(RANK_BACK_GUARD_KEY)
  }
  
  // 在路由切换完成后，如果是iOS设备，添加延时处理
  if (window.isIosDevice) {
    setTimeout(() => {
      // 触发窗口resize事件，让页面重新计算高度
      window.dispatchEvent(new Event('resize'))
    }, 200)
  }
})

export default router 
